import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, CheckCircle2, Ban, Undo2, Receipt } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Toolbar from '@/components/ui/Toolbar';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import { fmtMoney, fmtDate } from '@/lib/enums';
import InvoiceDetail from './InvoiceDetail';

const currentPeriod = () => new Date().toISOString().slice(0, 7);

const statusBadge = (inv, t) => {
  if (inv.status === 'paid') return <Badge variant="success">{t('invoices.statusPaid')}</Badge>;
  if (inv.status === 'void') return <Badge variant="neutral">{t('invoices.statusVoid')}</Badge>;
  if (inv.overdue) return <Badge variant="danger">{t('invoices.statusOverdue')}</Badge>;
  return <Badge variant="warning">{t('invoices.statusOpen')}</Badge>;
};

/** Faturas dos personais: gerar por competência, marcar paga, bloquear inadimplente. */
export default function AdminInvoices() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(currentPeriod());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get('/billing/invoices', period ? { period } : {})
      .then((res) => setRows((res.data || res).invoices || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [period]);

  const generate = async () => {
    setGenerating(true);
    setMsg('');
    setError('');
    try {
      const res = await api.post('/billing/invoices/generate', { period });
      const data = res.data || res;
      setMsg(t('invoices.generated', { count: data.generated }));
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const markPaid = async (inv) => {
    if (!confirm(t('invoices.confirmPaid', { name: inv.trainer?.name }))) return;
    try {
      await api.post(`/billing/invoices/${inv.id}/pay`, {});
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const toggleBlock = async (inv) => {
    const blocked = !!inv.trainer?.billingBlockedAt;
    if (!blocked && !confirm(t('invoices.confirmBlock', { name: inv.trainer?.name }))) return;
    try {
      await api.post(`/billing/trainers/${inv.trainerId}/${blocked ? 'unblock' : 'block'}`, {});
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const columns = [
    {
      key: 'trainer',
      header: t('invoices.colTrainer'),
      render: (r) => (
        <div>
          <strong style={{ color: 'var(--hi)' }}>{r.trainer?.name}</strong>
          {r.trainer?.billingBlockedAt && (
            <Badge variant="danger" style={{ marginLeft: 8 }}>{t('invoices.blockedTag')}</Badge>
          )}
        </div>
      ),
    },
    { key: 'period', header: t('invoices.colPeriod'), render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.period}</span> },
    { key: 'studentCount', header: t('invoices.colStudents') },
    { key: 'packagesSold', header: t('invoices.colSales') },
    { key: 'subscriptionAmount', header: t('invoices.colSubscription'), render: (r) => fmtMoney(r.subscriptionAmount) },
    { key: 'commissionAmount', header: t('invoices.colCommission'), render: (r) => fmtMoney(r.commissionAmount) },
    { key: 'totalAmount', header: t('invoices.colTotal'), render: (r) => <strong style={{ color: 'var(--volt)' }}>{fmtMoney(r.totalAmount)}</strong> },
    { key: 'dueDate', header: t('invoices.colDue'), render: (r) => fmtDate(r.dueDate) },
    { key: 'status', header: t('screens.status'), render: (r) => statusBadge(r, t) },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => {
        const blockLabel = r.trainer?.billingBlockedAt ? t('invoices.unblock') : t('invoices.block');
        // Só ícone + tooltip (padrão das tabelas) pra não quebrar a linha.
        return (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
            <Button size="sm" variant="ghost" icon={Receipt} title={t('invoices.details')}
              aria-label={t('invoices.details')} onClick={() => setDetail(r)} />
            {r.status === 'open' && (
              <Button size="sm" variant="ghost" icon={CheckCircle2} title={t('invoices.markPaid')}
                aria-label={t('invoices.markPaid')} onClick={() => markPaid(r)} />
            )}
            <Button size="sm" variant={r.trainer?.billingBlockedAt ? 'ghost' : 'danger'}
              icon={r.trainer?.billingBlockedAt ? Undo2 : Ban}
              title={blockLabel} aria-label={blockLabel}
              onClick={() => toggleBlock(r)} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.invoices')}>
        <Button icon={FileText} loading={generating} onClick={generate}>{t('invoices.generate')}</Button>
      </PageHeader>

      <Toolbar>
        <Input label={t('invoices.colPeriod')} type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        {msg && <span style={{ color: 'var(--volt)', fontSize: 13 }}>{msg}</span>}
        {error && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</span>}
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={<EmptyState icon={FileText} title={t('invoices.emptyTitle')} description={t('invoices.emptyDesc')} />}
      />

      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        kicker={detail?.period}
        title={detail?.trainer?.name || t('nav.invoices')}
        size="md"
      >
        {detail && <InvoiceDetail invoice={detail} />}
      </Modal>
    </>
  );
}
